import * as React from 'react';
import styles from './index.less';
import { Select, Space } from 'antd';
import FComponentsLib from '@freelog/components-lib';
import FDrawer from '@/components/FDrawer';
import { FI18n, FServiceAPI, FUtil } from '@freelog/tools-lib';
import * as AHooks from 'ahooks';
import FTooltip from '@/components/FTooltip';
import FCoverImage from '@/components/FCoverImage';
import dayjs from 'dayjs';
// import fCenterMessage from '@/components/fCenterMessage';
import FPolicyDisplay from '@/components/FPolicyDisplay';
import { PolicyFullInfo_Type } from '@/type/contractTypes';
// import fPolicyBuilder from '@/components/fPolicyBuilder';
import fMessage from '@/components/fMessage';
import img_choiceLabel from '@/assets/choice-label@2x.png';
import FHotspotTooltip, { fSetHotspotTooltipVisible } from '@/components/FHotspotTooltip';
import FIcons from '@/components/FIcons';
import FButton from '@/components/FButton';
import FSelect2 from '@/components/FSelect2';
import fPolicyBuilder3 from '@/components/fPolicyBuilder3';

interface FSignResourceToNodeDrawerProps {
  resourceIDs: string[];

  onOk?(value: {
    nodeID: number;
    allIsTheme: boolean;
    resultData: {
      [resourceID: string]: {
        status: 1 | 2;
        data: string;
      };
    };
  }): void;

  onClose?(): void;
}

interface FSignResourceToNodeDrawerStates {
  goodResources: {
    resourceID: string;
    resourceName: string;
    resourceTitle: string;
    cover: string;
    resourceType: string[];
    updateTime: string;
    selectedPolicy: {
      policyID: string;
      policyName: string;
    } | null;
    warning: '' | 'authException' | 'ownerFreeze';
    isChoice: boolean;
  }[];
  badResources: {
    resourceID: string;
    resourceName: string;
    resourceTitle: string;
    cover: string;
    resourceType: string[];
    updateTime: string;
    errorReason: string;
    isChoice: boolean;
  }[];
}

function FSignResourceToNodeDrawer({ resourceIDs, onClose, onOk }: FSignResourceToNodeDrawerProps) {

  const ref_container = React.useRef<HTMLDivElement>(null);

  const [$open, set$open, get$open] = FUtil.Hook.useGetState<boolean>(true);
  const [$selectNodeID, set$selectNodeID, get$selectNodeID] = FUtil.Hook.useGetState<number | null>(
    null,
  );
  const [$nodeOptions, set$nodeOptions, get$nodeOptions] = FUtil.Hook.useGetState<
    {
      value: number;
      label: string;
    }[]
  >([]);
  const [$goodResources, set$goodResources, get$goodResources] = FUtil.Hook.useGetState<
    FSignResourceToNodeDrawerStates['goodResources']
  >([]);
  const [$badResources, set$badResources, get$badResources] = FUtil.Hook.useGetState<
    FSignResourceToNodeDrawerStates['badResources']
  >([]);
  const [$selectingPolicyResourceID, set$selectingPolicyResourceID, get$selectingPolicyResourceID] =
    FUtil.Hook.useGetState<string>('');
  const [$loading, set$loading, get$loading] = FUtil.Hook.useGetState<boolean | string>(false);

  AHooks.useMount(async () => {
    const {
      data,
    }: {
      data: {
        dataList: {
          nodeDomain: string;
          nodeId: number;
          nodeName: string;
          nodeTitle: string;
          status: number;
        }[];
      };
    } = await FServiceAPI.Node.nodes({ limit: 100 });
    // console.log(data, 'data sdifj;lsdkjflksdjflkjsdlkjf');
    set$nodeOptions(
      data.dataList
        .filter((d) => {
          return d.status !== 4 && d.status !== 5;
        })
        .map((d) => {
          return {
            value: d.nodeId,
            label: d.nodeTitle || d.nodeName,
          };
        }),
    );
  });

  AHooks.useMount(() => {
    handleResource();
  });

  AHooks.useDebounceEffect(
    () => {
      if (!(!$selectNodeID || $goodResources.length === 0)) {
        fSetHotspotTooltipVisible(
          'result.createResourceSuccess.contractToNodeDrawer.confirmButton',
          {
            value: true,
            effectiveImmediately: true,
            onlyNullish: true,
          },
        );
      }
    },
    [$selectNodeID, $goodResources],
    {
      wait: 10,
    },
  );

  async function handleResource() {
    const {
      data: data_batchInfo,
    }: {
      data: any[];
    } = await FServiceAPI.Resource.batchInfo({
      resourceIds: resourceIDs.join(','),
    });
    const data_auth: {
      isAuth: boolean;
      resourceId: string;
    }[] = await batchAuth(
      data_batchInfo.map((dbi) => {
        return dbi.resourceId;
      }),
    );
    const params3: Parameters<typeof FServiceAPI.User.batchUserList>[0] = {
      userIds: data_batchInfo
        .map((dbi) => {
          return dbi.userId;
        })
        .join(','),
    };
    const {
      data: data_batchUserList,
    }: {
      data: {
        status: number;
        userId: number;
      }[];
    } = await FServiceAPI.User.batchUserList(params3);
    const goodResources: FSignResourceToNodeDrawerStates['goodResources'] = [];
    const badResources: FSignResourceToNodeDrawerStates['badResources'] = [];
    for (const d of data_batchInfo) {
      const isAuth: boolean =
        data_auth.find((da) => {
          return da.resourceId === d.resourceId;
        })?.isAuth || false;
      const ownerFreeze: boolean =
        data_batchUserList.find((dbu) => {
          return dbu.userId === d.userId;
        })?.status === 1;

      const resource: FSignResourceToNodeDrawerStates['goodResources'][number] =
        get$goodResources().find((gr) => {
          return gr.resourceID === d.resourceId;
        }) || {
          resourceID: d.resourceId,
          resourceName: d.resourceName,
          resourceTitle: d.resourceTitle,
          cover: d.coverImages[0] || '',
          resourceType: d.resourceType,
          updateTime: dayjs(d.updateDate).format(FUtil.Predefined.momentDateFormat),
          selectedPolicy: null,
          warning: ownerFreeze ? 'ownerFreeze' : !isAuth ? 'authException' : '',
          isChoice: d.operationType === 5,
          // addPolicy: null,
        };
      if (d.status === 0) {
        badResources.push({
          ...resource,
          errorReason: '未发行',
        });
        continue;
      }
      if (d.status === 2) {
        badResources.push({
          ...resource,
          errorReason: '已封禁',
        });
        continue;
      }
      if (d.status === 4) {
        badResources.push({
          ...resource,
          errorReason: '未上架',
        });
        continue;
      }

      if (get$selectNodeID()) {
        const {
          data: data_exhibit,
        }: {
          data: number | {};
        } = await FServiceAPI.Exhibit.presentableDetails({
          resourceId: d.resourceId,
          nodeId: get$selectNodeID() || -1,
        });

        if (data_exhibit) {
          badResources.push({
            ...resource,
            // errorReason: '已签约',
            errorReason: FI18n.i18nNext.t('addresourcetonode_addtonode_error_addedbefore'),
          });
          continue;
        }
      }
      goodResources.push(resource);
    }

    set$goodResources(goodResources);
    set$badResources(badResources);
  }

  return (
    <FDrawer
      title={
        // 添加至节点
        FI18n.i18nNext.tAuto('addresourcetonode_addtonode_title')
      }
      open={$open}
      loading={$loading}
      topRight={
        <Space size={30}>
          <FComponentsLib.FTextBtn
            type='default'
            onClick={() => {
              set$open(false);
            }}
          >
            {
              // 取消
              FI18n.i18nNext.tAuto('btn_cancel')
            }
          </FComponentsLib.FTextBtn>
          <FHotspotTooltip
            id={'result.createResourceSuccess.contractToNodeDrawer.confirmButton'}
            style={{ right: 16, bottom: -40 }}
            text={FI18n.i18nNext.tAuto('hotpots_addresourcetonode_done')}
            onOpenChange={(o) => {
              if (o) {
                fSetHotspotTooltipVisible(
                  'result.createResourceSuccess.contractToNodeDrawer.confirmButton',
                  {
                    value: false,
                    effectiveImmediately: false,
                    onlyNullish: false,
                  },
                );
              }
            }}
          >
            <FComponentsLib.FRectBtn
              disabled={!$selectNodeID || $goodResources.length === 0}
              type='primary'
              onClick={async () => {
                fSetHotspotTooltipVisible(
                  'result.createResourceSuccess.contractToNodeDrawer.confirmButton',
                  {
                    value: false,
                    effectiveImmediately: true,
                    onlyNullish: false,
                  },
                );
                set$loading(
                  // '正在将资源添加至节点'
                  FI18n.i18nNext.t('addresourcetonode_addtonode_msg_wait_for_processing02'),
                );
                const {
                  ret,
                  errCode,
                  data,
                  msg,
                }: {
                  ret: number;
                  errCode: number;
                  data: {
                    [resourceID: string]: {
                      status: 1 | 2;
                      data: string;
                    };
                  };
                  msg: string;
                } = await FServiceAPI.Exhibit.batchCreatePresentable({
                  nodeId: get$selectNodeID() || -1,
                  resources: get$goodResources().map((r) => {
                    return {
                      resourceId: r.resourceID,
                      policyId: !!r.selectedPolicy ? r.selectedPolicy.policyID : undefined,
                    };
                  }),
                });

                await FUtil.Tool.promiseSleep(500);
                if (ret !== 0 || errCode !== 0) {
                  // fCenterMessage({ message: msg });
                  fMessage(msg, 'error');
                  set$open(false);
                  return;
                }
                onOk?.({
                  nodeID: get$selectNodeID() || -1,
                  allIsTheme: get$goodResources().every((r) => {
                    return r.resourceType.includes('主题');
                  }),
                  resultData: data,
                });
                set$open(false);
                set$loading(false);
              }}
            >
              {/* 确定 */}
              {FI18n.i18nNext.tAuto('btn_done')}
            </FComponentsLib.FRectBtn>
          </FHotspotTooltip>
        </Space>
      }
      afterOpenChange={(v) => {
        if (!v) {
          onClose && onClose();
        }
      }}
    >
      <div style={{ position: 'relative' }} ref={ref_container}>
        <FComponentsLib.FTitleText
          text={
            // 选择节点
            FI18n.i18nNext.t('addresourcetonode_addtonode_selectnode')
          }
          type={'h3'}
        />
        <div style={{ height: 10 }} />
        {$nodeOptions.length === 0 ? (
          <div className={styles.nodeSelector}>
            <Space size={10}>
              <span className={styles.nodeSelectorLabel}>您还没有创建节点</span>
              <FComponentsLib.FTextBtn
                type='primary'
                onClick={() => {
                  self.open(FUtil.LinkTo.nodeCreator());
                }}
              >
                创建节点
              </FComponentsLib.FTextBtn>
            </Space>
          </div>
        ) : (
          <FSelect2
          // open={true}
            getPopupContainer={() => {
              return ref_container.current || self.document.body;
            }}
            value={$selectNodeID}
            // placeholder={'选择签约的节点'}
            placeholder={FI18n.i18nNext.t('add2node_msg_plsselectnode')}
            style={{ width: '100%', height: 38, borderRadius: 4 }}
            onChange={async (value) => {
              set$selectNodeID(value);
              set$loading(
                // '校验中，请稍后…'
                FI18n.i18nNext.t('addresourcetonode_addtonode_msg_wait_for_processing'),
              );
              await handleResource();
              await FUtil.Tool.promiseSleep(500);
              set$loading(false);
            }}
            options={$nodeOptions}
            popupRender={(menu) => (
              <>
                {menu}
                {/*<Divider style={{ margin: '8px 0' }} />*/}
                {$nodeOptions.length < 3 && (
                  <a
                    href={FUtil.LinkTo.nodeCreator()}
                    className={styles.newButton}
                    target={'_blank'}
                  >
                    <Space size={10}>
                      <FIcons.FPlus style={{ fontSize: 14 }} />
                      <span>创建节点</span>
                    </Space>
                  </a>
                )}
              </>
            )}
          />
        )}

        {$goodResources.length > 0 && (
          <>
            <div style={{ height: 30 }} />
            <Space size={5}>
              <FComponentsLib.FTitleText
                text={
                  // '确认资源及策略'
                  FI18n.i18nNext.t('addresourcetonode_addtonode_resources')
                }
                type={'h3'}
              />

              <FTooltip
                title={
                  // '系统为当前资源创建并启用“永久免费”的授权策略，以此策略自动将当前资源作为展品签约添加到节点；展品添加成功，页面跳转至【节点管理 - 展品管理】模块，同系统自动停用针对一键签约创建的“永久免费”授权策略。'
                  FI18n.i18nNext.tJSXElement('addresourcetonode_addtonode_resources_info')
                }
              >
                <div style={{ cursor: 'pointer' }}>
                  <FIcons.FInfo style={{ fontSize: 14 }} />
                </div>
              </FTooltip>
            </Space>
            <div style={{ height: 10 }} />
            <Space size={10} direction={'vertical'} style={{ width: '100%' }}>
              {$goodResources.map((r) => {
                return (
                  <div className={styles.resourceItem} key={r.resourceID}>
                    <div style={{ position: 'relative' }}>
                      <FCoverImage src={r.cover} width={104} style={{ borderRadius: 4 }} />
                      {r.isChoice && (
                        <div
                          style={{
                            position: 'absolute',
                            top: 2,
                            left: 5,
                          }}
                        >
                          <FTooltip title={'编辑精选'} placement={'bottomLeft'}>
                            <img src={img_choiceLabel} alt={''} style={{ width: 16, height: 16 }} />
                          </FTooltip>
                        </div>
                      )}
                    </div>
                    <div>
                      <FComponentsLib.FTitleText
                        type={'h3'}
                        text={r.resourceTitle || r.resourceName}
                        style={{ maxWidth: 500 }}
                        singleRow={true}
                      />
                      <div style={{ height: 7 }} />
                      <FComponentsLib.FContentText
                        type={'additional2'}
                        // text={}
                        text={
                          // `资源类型 ${r.resourceType.join('/')}｜更新时间 ${
                          //   r.updateTime
                          // }`
                          `${FI18n.i18nNext.tAuto(
                            'addresourcetonode_addtonode_lable_resourcetype',
                          )} ${r.resourceType.join('/')}｜${FI18n.i18nNext.tAuto(
                            'addresourcetonode_addtonode_label_lastupdated',
                          )} ${r.updateTime}`
                        }
                      />

                      {r.warning === 'authException' && (
                        <>
                          <div style={{ height: 7 }} />
                          <div
                            style={{
                              fontWeight: 600,
                              fontSize: 12,
                              color: '#E9A923',
                              lineHeight: '18px',
                            }}
                          >
                            {FI18n.i18nNext.tAuto('alarm_resource_authorization_abnormal')}
                          </div>
                        </>
                      )}

                      {r.warning === 'ownerFreeze' && (
                        <>
                          <div style={{ height: 7 }} />
                          <div
                            style={{
                              fontWeight: 600,
                              fontSize: 12,
                              color: '#E9A923',
                              lineHeight: '18px',
                            }}
                          >
                            {FI18n.i18nNext.tAuto('alert_auth_useraccountdisable_resouce')}
                          </div>
                        </>
                      )}
                      <div style={{ height: 7 }} />
                      <Space size={20}>
                        <label className={styles.policyLabel}>
                          <FIcons.FPolicy style={{ fontSize: 12 }} />
                          {r.selectedPolicy && <span>{r.selectedPolicy.policyName}</span>}
                          {!r.selectedPolicy && <span>永久免费</span>}
                        </label>
                        <FComponentsLib.FTextBtn
                          type={'primary'}
                          style={{ fontSize: 12 }}
                          onClick={() => {
                            set$selectingPolicyResourceID(r.resourceID);
                          }}
                        >
                          {/* 更换授权策略 */}
                          {FI18n.i18nNext.tAuto('addresourcetonode_addtonode_btn_changeplan')}
                        </FComponentsLib.FTextBtn>
                      </Space>
                    </div>
                  </div>
                );
              })}
            </Space>
          </>
        )}

        {$badResources.length > 0 && (
          <>
            <div style={{ height: 30 }} />
            <FComponentsLib.FTitleText
              text={
                // 以下资源无法完成自动签约
                FI18n.i18nNext.t('addresourcetonode_addtonode_list_error')
              }
              type={'h3'}
            />
            <div style={{ height: 10 }} />
            <Space size={10} direction={'vertical'} style={{ width: '100%' }}>
              {$badResources.map((r) => {
                return (
                  <div className={styles.resourceItem} key={r.resourceID}>
                    <div style={{ position: 'relative' }}>
                      <FCoverImage src={r.cover} width={104} style={{ borderRadius: 4 }} />
                      {r.isChoice && (
                        <div
                          style={{
                            position: 'absolute',
                            top: 2,
                            left: 5,
                          }}
                        >
                          <FTooltip title={'编辑精选'} placement={'bottomLeft'}>
                            <img src={img_choiceLabel} alt={''} style={{ width: 16, height: 16 }} />
                          </FTooltip>
                        </div>
                      )}
                    </div>
                    <div style={{ width: 350 }}>
                      <FComponentsLib.FTitleText
                        type={'h3'}
                        text={r.resourceTitle || r.resourceName}
                        style={{ maxWidth: 350 }}
                        singleRow={true}
                      />
                      <div style={{ height: 10 }} />
                      <FComponentsLib.FContentText
                        type={'additional2'}
                        // text={`资源类型 ${r.resourceType.join('/')}｜更新时间 ${r.updateTime}`}
                        text={`${FI18n.i18nNext.tAuto(
                          'addresourcetonode_addtonode_lable_resourcetype',
                        )} ${r.resourceType.join('/')}｜${FI18n.i18nNext.tAuto(
                          'addresourcetonode_addtonode_label_lastupdated',
                        )} ${r.updateTime}`}
                      />
                    </div>
                    <div className={styles.resourceItemError}>{r.errorReason}</div>
                  </div>
                );
              })}
            </Space>
          </>
        )}

        <div style={{ height: 100 }} />

        {$badResources.length > 0 && (
          <div className={styles.tip}>
            <FIcons.FExclamation
              style={{
                fontSize: 16,
                color: '#E9A923',
              }}
            />
            <span>
              {
                // 已选择的资源中，部分资源无法自动签约，但不影响其他资源签约
                FI18n.i18nNext.tJSXElement('addresourcetonode_msg_partialabnormal')
              }
            </span>
          </div>
        )}
      </div>

      <SelectPolicyDrawer
        resourceID={$selectingPolicyResourceID}
        onCancel={() => {
          set$selectingPolicyResourceID('');
        }}
        onSelected={(value) => {
          set$goodResources(
            get$goodResources().map((resource) => {
              if (get$selectingPolicyResourceID() !== resource.resourceID) {
                return resource;
              }
              return {
                ...resource,
                selectedPolicy: value,
                addPolicy: null,
              };
            }),
          );
          set$selectingPolicyResourceID('');
        }}
      />
    </FDrawer>
  );
}

export default FSignResourceToNodeDrawer;

interface SelectPolicyDrawerProps {
  resourceID: string;

  onSelected?(value: { policyID: string; policyName: string }): void;

  onCancel?(): void;
}

function SelectPolicyDrawer({ resourceID, onSelected, onCancel }: SelectPolicyDrawerProps) {
  const [$policyFullInfo, set$policyFullInfo, get$policyFullInfo] = FUtil.Hook.useGetState<
    PolicyFullInfo_Type[]
  >([]);

  async function handleData() {
    const { data } = await FServiceAPI.Resource.info({
      resourceIdOrName: resourceID,
      isLoadPolicyInfo: 1,
      isTranslate: 1,
    });
    // console.log(data, 'data sdifjl;sdkjfl;ksjdal;kfjsdfikjweo;ikfjlksadjflkj');
    set$policyFullInfo(data.policies as PolicyFullInfo_Type[]);
  }

  async function onAddPolicy() {
    const result = await fPolicyBuilder3({
      alreadyUsedTexts: get$policyFullInfo().map((p) => {
        return p.policyText;
      }),
      alreadyUsedTitles: get$policyFullInfo().map((p) => {
        return p.policyName;
      }),
      // targetType: 'resource',
    });
    if (!result) {
      return;
    }
    // onAdd && onAdd(result);
    const {
      ret,
      errCode,
      msg,
      data,
    }: {
      ret: number;
      errCode: number;
      msg: string;
      data: {
        policies: {
          policyId: string;
          policyName: string;
          status: 0 | 1;
        }[];
      };
    } = await FServiceAPI.Resource.update({
      resourceId: resourceID,
      addPolicies: [
        {
          policyName: result.title,
          policyText: result.text,
          status: 1,
        },
      ],
    });

    if (ret !== 0 || errCode !== 0) {
      fMessage(msg, 'error');
      return;
    }

    const policy = data.policies.find((p) => {
      return p.policyName === result.title;
    });

    if (!policy) {
      return;
    }
    onSelected &&
      onSelected({
        policyID: policy.policyId,
        policyName: policy.policyName,
      });
  }

  return (
    <FDrawer
      open={resourceID !== ''}
      title={FI18n.i18nNext.tAuto('addresourcetonode_changeauthplan_title')}
      topRight={
        <FComponentsLib.FRectBtn
          type={'default'}
          onClick={() => {
            onCancel && onCancel();
          }}
        >
          取消
        </FComponentsLib.FRectBtn>
      }
      afterOpenChange={(o) => {
        if (o) {
          handleData();
        }
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Space size={5}>
          <FIcons.FPolicy style={{ fontSize: 14, color: '#222' }} />
          <FComponentsLib.FContentText
            type={'additional2'}
            // text={'选择策略'}
            text={FI18n.i18nNext.t('addresourcetonode_changeauthplan_list_authplans')}
            style={{ color: '#222' }}
          />
        </Space>
        <Space size={5}>
          <FButton.FCircleBtn
            type={'primary'}
            size={'small'}
            onClick={() => {
              onAddPolicy();
            }}
          />
          <FComponentsLib.FTextBtn
            type={'primary'}
            onClick={() => {
              onAddPolicy();
            }}
          >
            新建授权策略
          </FComponentsLib.FTextBtn>
        </Space>
      </div>
      <div style={{ height: 10 }} />
      <Space size={10} direction={'vertical'} style={{ width: '100%' }}>
        {$policyFullInfo.map((p) => {
          return (
            <div className={styles.policyCard} key={p.policyId}>
              <div className={styles.policyCardTitle}>
                <FComponentsLib.FContentText text={p.policyName} type={'highlight'} />
                <FComponentsLib.FRectBtn
                  onClick={() => {
                    onSelected &&
                      onSelected({
                        policyID: p.policyId,
                        policyName: p.policyName,
                      });
                  }}
                >
                  {FI18n.i18nNext.tAuto('addresourcetonode_changeauthplan_btn_select')}
                </FComponentsLib.FRectBtn>
              </div>
              <div style={{ height: 25 }} />
              <FPolicyDisplay fullInfo={p} />
            </div>
          );
        })}
      </Space>
    </FDrawer>
  );
}

async function batchAuth(resourceIds: string[]): Promise<
  {
    isAuth: boolean;
    resourceId: string;
  }[]
> {
  // console.log(resourceIds.length, 'resourceIds.lengthwiesdofj;lksdjfl;jsdlk;jfl;kjsdlk;jl');
  const idss: string[][] = Array.from({ length: Math.ceil(resourceIds.length / 100) }, (_, i) => {
    return resourceIds.slice(i * 100, (i + 1) * 100);
  });

  // console.log(ids, 'ids isdjflksdjf;lkjdsl;kfjl;ksdjk');

  const promises: Promise<{
    data: {
      isAuth: boolean;
      resourceId: string;
    }[];
  }>[] = idss.map((ids) => {
    return FServiceAPI.Resource.batchAuth({
      resourceIds: ids.join(','),
    });
  });

  // const { data: data_auth }:
  const result: {
    data: {
      isAuth: boolean;
      resourceId: string;
    }[];
  }[] = await Promise.all(promises);

  return result
    .map((r) => {
      return r.data;
    })
    .flat();
}
